import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { CheckCircle2, Star, Crown, Clock, Users, Bell, HeadphonesIcon } from "lucide-react";

export default function PostRequirement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState("free");
  const [form, setForm] = useState({
    title: "",
    business_type: "",
    industry: "",
    budget_min: "",
    budget_max: "",
    location: "",
    timeline: "",
    description: "",
    contact_preference: "platform"
  });

  const industries = ["Technology", "Retail", "Manufacturing", "Healthcare", "Food & Beverage", "Real Estate", "Services", "Education", "Other"];
  const timelines = ["Within 1 month", "1-3 months", "3-6 months", "6-12 months", "No rush"];

  const tiers = [
    {
      id: "free",
      name: "Free Basic",
      price: 0,
      icon: CheckCircle2,
      color: "bg-slate-50 border-slate-200",
      features: [
        "7 days visibility",
        "Public listing",
        "Self-managed responses",
        "Basic exposure"
      ]
    },
    {
      id: "premium",
      name: "Premium",
      price: 1999,
      icon: Star,
      color: "bg-blue-50 border-blue-300",
      popular: true,
      features: [
        "30 days visibility",
        "Featured placement",
        "Email notifications",
        "Priority matching",
        "Highlighted listing"
      ]
    },
    {
      id: "concierge",
      name: "Concierge",
      price: 9999,
      icon: Crown,
      color: "bg-purple-50 border-purple-300",
      features: [
        "60 days visibility",
        "Dedicated manager",
        "Active business hunting",
        "Background verification",
        "Negotiation support",
        "Full transaction support"
      ]
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please login to post requirement");
      navigate("/auth");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        budget_min: parseFloat(form.budget_min),
        budget_max: parseFloat(form.budget_max),
        tier: selectedTier
      };

      await api.post("/requirements", payload);
      
      if (selectedTier === "free") {
        toast.success("Requirement posted successfully!");
        navigate("/dashboard");
      } else {
        toast.success("Requirement created! Proceeding to payment...");
        // TODO: Integrate Razorpay payment
        setTimeout(() => navigate("/dashboard"), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post requirement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12" data-testid="post-requirement-header">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Post Your Business Requirement</h1>
          <p className="text-lg text-slate-600">Tell us what business you're looking for, and we'll connect you with relevant sellers</p>
        </div>

        {/* Pricing Tiers */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Choose Your Plan</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier) => {
              const Icon = tier.icon;
              return (
                <Card
                  key={tier.id}
                  className={`cursor-pointer transition-all ${tier.color} ${
                    selectedTier === tier.id ? "ring-4 ring-blue-500 shadow-xl" : ""
                  }`}
                  onClick={() => setSelectedTier(tier.id)}
                  data-testid={`tier-${tier.id}`}
                >
                  <CardContent className="p-6">
                    {tier.popular && (
                      <div className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-3">
                        MOST POPULAR
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className="w-8 h-8 text-blue-600" />
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{tier.name}</h3>
                        <p className="text-3xl font-bold text-slate-900">
                          {tier.price === 0 ? "Free" : `₹${tier.price.toLocaleString()}`}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Requirement Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Requirement Details</CardTitle>
            <CardDescription>Provide detailed information to help us find the perfect match</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Requirement Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Looking for Coffee Shop in Mumbai"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  data-testid="title-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="business_type">Business Type *</Label>
                  <Input
                    id="business_type"
                    placeholder="e.g., Restaurant, Retail Store, Startup"
                    value={form.business_type}
                    onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                    required
                    data-testid="business-type-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Select value={form.industry} onValueChange={(value) => setForm({ ...form, industry: value })} required>
                    <SelectTrigger data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Budget Range *</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="number"
                    placeholder="Minimum (₹)"
                    value={form.budget_min}
                    onChange={(e) => setForm({ ...form, budget_min: e.target.value })}
                    required
                    data-testid="budget-min-input"
                  />
                  <Input
                    type="number"
                    placeholder="Maximum (₹)"
                    value={form.budget_max}
                    onChange={(e) => setForm({ ...form, budget_max: e.target.value })}
                    required
                    data-testid="budget-max-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="location">Preferred Location *</Label>
                  <Input
                    id="location"
                    placeholder="e.g., Mumbai, Bangalore, Delhi"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    required
                    data-testid="location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeline">Timeline *</Label>
                  <Select value={form.timeline} onValueChange={(value) => setForm({ ...form, timeline: value })} required>
                    <SelectTrigger data-testid="timeline-select">
                      <SelectValue placeholder="When do you want to buy?" />
                    </SelectTrigger>
                    <SelectContent>
                      {timelines.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Detailed Requirements *</Label>
                <Textarea
                  id="description"
                  rows={6}
                  placeholder="Describe your requirements in detail - location preferences, must-have features, deal-breakers, etc."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                <h3 className="font-semibold text-slate-900 mb-2">Selected Plan: {tiers.find(t => t.id === selectedTier)?.name}</h3>
                <p className="text-sm text-slate-600 mb-4">
                  {selectedTier === "free" && "Your requirement will be visible for 7 days"}
                  {selectedTier === "premium" && "Your requirement will be featured for 30 days with priority placement"}
                  {selectedTier === "concierge" && "You'll get dedicated support and active business hunting for 60 days"}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  Total: {tiers.find(t => t.id === selectedTier)?.price === 0 ? "Free" : `₹${tiers.find(t => t.id === selectedTier)?.price.toLocaleString()}`}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                disabled={loading}
                data-testid="submit-requirement-button"
              >
                {loading ? "Posting..." : selectedTier === "free" ? "Post Requirement" : `Proceed to Payment`}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
